import React, { useState } from "react";
import {
  Form,
  Rate,
  Input,
  Upload,
  Button,
  Checkbox,
  Space,
  Typography,
  message,
  Alert,
  Card,
  Progress,
} from "antd";
import {
  StarOutlined,
  UploadOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import type { UploadFile, RcFile } from "antd/es/upload/interface";
import API_CONFIG from "../../config/api";

const { TextArea } = Input;
const { Text, Title } = Typography;

/**
 * Props du composant ReviewForm
 */
interface ReviewFormProps {
  /** ID du produit à évaluer */
  productId: number;

  /** ID de la commande (pour vérification achat) */
  orderId: number;

  /** Nom du produit (affichage) */
  productName: string;

  /** Token JWT du client authentifié */
  customerToken: string;

  /** Callback succès après soumission */
  onSuccess?: () => void;

  /** Callback annulation */
  onCancel?: () => void;
}

/**
 * Composant ReviewForm
 *
 * Formulaire de soumission d'avis client avec upload de photos/vidéos
 *
 * @example
 * <ReviewForm
 *   productId={123}
 *   orderId={456}
 *   productName="Smartphone XYZ"
 *   customerToken={token}
 *   onSuccess={() => message.success("Avis soumis !")}
 *   onCancel={() => closeModal()}
 * />
 */
const ReviewForm: React.FC<ReviewFormProps> = ({
  productId,
  orderId,
  productName,
  customerToken,
  onSuccess,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [rating, setRating] = useState(0);
  const [photoList, setPhotoList] = useState<UploadFile[]>([]);
  const [videoFile, setVideoFile] = useState<UploadFile | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  /**
   * Validation avant upload de photos
   */
  const beforePhotoUpload = (file: RcFile): boolean => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("Vous ne pouvez uploader que des images (JPG, PNG, GIF, WEBP)");
      return false;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("La photo doit faire moins de 5MB");
      return false;
    }

    if (photoList.length >= 5) {
      message.error("Maximum 5 photos autorisées");
      return false;
    }

    return false; // On gère manuellement l'upload
  };

  /**
   * Validation avant upload de vidéo
   */
  const beforeVideoUpload = (file: RcFile): boolean => {
    const isVideo = file.type.startsWith("video/");
    if (!isVideo) {
      message.error("Vous ne pouvez uploader que des vidéos (MP4, AVI, MOV, WEBM)");
      return false;
    }

    const isLt50M = file.size / 1024 / 1024 < 50;
    if (!isLt50M) {
      message.error("La vidéo doit faire moins de 50MB");
      return false;
    }

    if (videoFile) {
      message.error("Maximum 1 vidéo autorisée");
      return false;
    }

    return false; // On gère manuellement l'upload
  };

  /**
   * Gère l'ajout de photos
   */
  const handlePhotoChange = (info: any) => {
    const { fileList } = info;
    setPhotoList(fileList.slice(-5)); // Max 5 photos
  };

  /**
   * Gère l'ajout de vidéo
   */
  const handleVideoChange = (info: any) => {
    const { fileList } = info;
    if (fileList.length > 0) {
      setVideoFile(fileList[0]);
    } else {
      setVideoFile(null);
    }
  };

  /**
   * Supprime une photo
   */
  const removePhoto = (file: UploadFile) => {
    setPhotoList((prev) => prev.filter((item) => item.uid !== file.uid));
  };

  /**
   * Supprime la vidéo
   */
  const removeVideo = () => {
    setVideoFile(null);
  };

  /**
   * Soumet le formulaire avec upload des médias
   */
  const handleSubmit = async (values: any) => {
    if (rating === 0) {
      message.error("Veuillez donner une note");
      return;
    }

    if (!values.comment || values.comment.trim().length < 20) {
      message.error("Le commentaire doit contenir au moins 20 caractères");
      return;
    }

    if (!values.consent) {
      message.error("Vous devez accepter que votre avis soit publié");
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append("orderId", orderId.toString());
      formData.append("productId", productId.toString());
      formData.append("rating", rating.toString());
      formData.append("comment", values.comment.trim());

      if (values.title) {
        formData.append("title", values.title.trim());
      }

      // Ajouter les photos
      photoList.forEach((file) => {
        if (file.originFileObj) {
          formData.append("photos", file.originFileObj);
        }
      });

      // Ajouter la vidéo
      if (videoFile && videoFile.originFileObj) {
        formData.append("video", videoFile.originFileObj);
      }

      const response = await fetch(`${API_CONFIG.PUBLIC_CUSTOMER_API}/reviews`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${customerToken}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        message.success(
          "Merci ! Votre avis sera publié après modération.",
          5
        );
        form.resetFields();
        setRating(0);
        setPhotoList([]);
        setVideoFile(null);

        if (onSuccess) {
          onSuccess();
        }
      } else {
        message.error(data.message || "Erreur lors de la soumission");
      }
    } catch (error) {
      console.error("Erreur soumission avis:", error);
      message.error("Impossible de soumettre votre avis");
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  /**
   * Annule le formulaire
   */
  const handleCancel = () => {
    form.resetFields();
    setRating(0);
    setPhotoList([]);
    setVideoFile(null);

    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Card
      title={
        <Space direction="vertical" size={4}>
          <Title level={4} style={{ margin: 0 }}>
            Donner votre avis
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            sur {productName}
          </Text>
        </Space>
      }
      style={{ maxWidth: 700, margin: "0 auto" }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        {/* Note étoiles */}
        <Form.Item
          label={<Text strong>Note *</Text>}
          required
          rules={[
            {
              validator: () =>
                rating > 0
                  ? Promise.resolve()
                  : Promise.reject("Veuillez donner une note"),
            },
          ]}
        >
          <Space direction="vertical" size={4}>
            <Rate
              value={rating}
              onChange={setRating}
              style={{ fontSize: 32, color: "#faad14" }}
              character={<StarOutlined />}
            />
            <Text type="secondary" style={{ fontSize: 13 }}>
              {rating === 0 && "Sélectionnez une note"}
              {rating === 1 && "Très insatisfait"}
              {rating === 2 && "Insatisfait"}
              {rating === 3 && "Moyen"}
              {rating === 4 && "Satisfait"}
              {rating === 5 && "Très satisfait"}
            </Text>
          </Space>
        </Form.Item>

        {/* Titre (optionnel) */}
        <Form.Item
          name="title"
          label={<Text strong>Titre de votre avis (optionnel)</Text>}
        >
          <Input
            placeholder="Ex: Excellent rapport qualité/prix"
            maxLength={50}
            showCount
          />
        </Form.Item>

        {/* Commentaire */}
        <Form.Item
          name="comment"
          label={<Text strong>Votre commentaire *</Text>}
          rules={[
            { required: true, message: "Le commentaire est obligatoire" },
            {
              min: 20,
              message: "Le commentaire doit contenir au moins 20 caractères",
            },
            {
              max: 1000,
              message: "Le commentaire ne peut dépasser 1000 caractères",
            },
          ]}
        >
          <TextArea
            rows={6}
            placeholder="Partagez votre expérience avec ce produit (minimum 20 caractères)..."
            maxLength={1000}
            showCount
          />
        </Form.Item>

        {/* Upload photos */}
        <Form.Item label={<Text strong>Photos (optionnel, max 5)</Text>}>
          <Upload
            listType="picture-card"
            fileList={photoList}
            beforeUpload={beforePhotoUpload}
            onChange={handlePhotoChange}
            onRemove={removePhoto}
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple
          >
            {photoList.length < 5 && (
              <div>
                <PictureOutlined style={{ fontSize: 24 }} />
                <div style={{ marginTop: 8 }}>Ajouter photo</div>
              </div>
            )}
          </Upload>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Formats acceptés : JPG, PNG, GIF, WEBP (max 5MB chacun)
          </Text>
        </Form.Item>

        {/* Upload vidéo */}
        <Form.Item label={<Text strong>Vidéo (optionnel, max 1)</Text>}>
          <Upload
            listType="picture-card"
            fileList={videoFile ? [videoFile] : []}
            beforeUpload={beforeVideoUpload}
            onChange={handleVideoChange}
            onRemove={removeVideo}
            accept="video/mp4,video/avi,video/mov,video/webm"
          >
            {!videoFile && (
              <div>
                <VideoCameraOutlined style={{ fontSize: 24 }} />
                <div style={{ marginTop: 8 }}>Ajouter vidéo</div>
              </div>
            )}
          </Upload>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Formats acceptés : MP4, AVI, MOV, WEBM (max 50MB)
          </Text>
        </Form.Item>

        {/* Progression upload */}
        {loading && uploadProgress > 0 && (
          <Progress percent={uploadProgress} status="active" />
        )}

        {/* Consentement publication */}
        <Form.Item
          name="consent"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject("Vous devez accepter la publication"),
            },
          ]}
        >
          <Checkbox>
            J'accepte que mon avis soit publié publiquement sur la boutique
          </Checkbox>
        </Form.Item>

        {/* Informations */}
        <Alert
          message="Votre avis sera modéré avant publication"
          description="Nous vérifions tous les avis pour garantir leur authenticité. Votre avis sera publié sous 24-48h."
          type="info"
          showIcon
          style={{ marginBottom: 20 }}
        />

        {/* Boutons actions */}
        <Form.Item style={{ marginBottom: 0 }}>
          <Space size={12}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
            >
              Soumettre mon avis
            </Button>
            <Button onClick={handleCancel} size="large" disabled={loading}>
              Annuler
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default ReviewForm;
